import React, { useState, useEffect } from 'react';
import { Gift, Tag } from 'lucide-react';
import { PlanDetails } from '../../types/api';
import PaymentTypeSection from './PaymentTypeSection';
import { useCurrencyState } from '../../stores/subscriptionSelectors';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../../services';
import configService from '../../services/configService';
import { formatPrice } from '../../utils/priceFormat';
// Import fruit images
import standardFruitImage from '../../assets/images/fruits/Fruits-Standard.png';
import premiumFruitImage from '../../assets/images/fruits/Fruits-Premium.png';

import standardFlowerImage from '../../assets/images/flowers/flowers_small.png';
import premiumFlowerImage from '../../assets/images/flowers/flowers_large.png';

// Import cat grass images
import catGrassSmallImage from '../../assets/images/catGrass/catGrass_small.png';

interface SubscriptionPlanProps {
  subscriptionType: 'monthly' | 'yearly' | 'trial';
  setSubscriptionType: (type: 'monthly' | 'yearly' | 'trial') => void;
  subscriptionLevel: 'standard' | 'premium';
  setSubscriptionLevel: (level: 'standard' | 'premium') => void;
  planDetails: PlanDetails | null;
  loading: boolean;
  error: string | null;
  vaseImage: string;
  displayMode?: 'plan-level' | 'billing-period' | 'both';
  paymentType: 'one_time' | 'subscription';
  setPaymentType: (type: 'one_time' | 'subscription') => void;
  selectedPaymentMethod: string;
  serviceType: 'flower' | 'fruit' | 'cat_grass';
  // Add promotion code related props
  promotionCode?: string;
  promotionDetails?: {
    isValid: boolean;
    isLoaded: boolean;
    percentOff: number;
    amountOff: number;
    description: string;
    currency?: string | null;
    minimumAmount?: number;
    errorMessage?: string;
    duration?: 'once' | 'forever' | 'repeating';
  };
  getEffectivePromotionCode?: (priceBeforePromotion: number) => string | null;
}

export const SubscriptionPlanSection = ({
  subscriptionType,
  setSubscriptionType,
  subscriptionLevel,
  setSubscriptionLevel,
  planDetails,
  loading,
  error,
  vaseImage,
  displayMode = 'both',
  paymentType,
  setPaymentType,
  selectedPaymentMethod,
  serviceType,
  // Add promotion code props with defaults
  promotionCode = '',
  promotionDetails = { isValid: false, isLoaded: true, percentOff: 0, amountOff: 0, description: '', duration: 'once' },
  getEffectivePromotionCode
}: SubscriptionPlanProps) => {
  // Get currency information
  const { currency, availableCurrencies } = useCurrencyState();

  const { t } = useTranslation();
  
  // Get currency symbol
  const currencySymbol = availableCurrencies.find(c => c.code === currency)?.symbol || '฿';
  
  // Get USD prices if available
  const useUSD = currency === 'usd';
  
  // State to store original pricing from config service
  const [originalRegularPricing, setOriginalRegularPricing] = useState<any>(null);
  const [trialOriginalPricing, setTrialOriginalPricing] = useState<any>(null);
  
  // Fetch original pricing configurations from config service
  useEffect(() => {
    const fetchOriginalPricing = async () => {
      try {
        const [regularPricing, trialPricing] = await Promise.all([
          configService.getOriginalRegularPricing(serviceType),
          configService.getTrialOriginalPricing(serviceType)
        ]);
        
        setOriginalRegularPricing(regularPricing);
        setTrialOriginalPricing(trialPricing);
      } catch (error) {
        console.error('Error fetching original pricing:', error);
        // Keep default null values so hardcoded fallbacks are used
      }
    };

    fetchOriginalPricing();
  }, [serviceType]);
  
  // Helper function to get original price from config service or fallback to hardcoded
  const getOriginalPriceFromConfig = (subscriptionLevel: string, currency: string) => {
    if (originalRegularPricing) {
      const levelPricing = originalRegularPricing[subscriptionLevel as 'standard' | 'premium'];
      return levelPricing?.[currency as 'thb' | 'usd'] || 
        (serviceType === 'flower'
          ? (subscriptionLevel === 'premium' ? (currency === 'usd' ? 24.99 : 599) : (currency === 'usd' ? 19.99 : 399))
          : serviceType === 'cat_grass' 
          ? (subscriptionLevel === 'premium' ? (currency === 'usd' ? 11.99 : 349) : (currency === 'usd' ? 7.99 : 249))
          : (subscriptionLevel === 'premium' ? (currency === 'usd' ? 42.99 : 1299) : (currency === 'usd' ? 32.99 : 999)));
    } else {
      // Fallback to hardcoded values
      return serviceType === 'flower'
        ? (subscriptionLevel === 'premium' ? (currency === 'usd' ? 24.99 : 599) : (currency === 'usd' ? 19.99 : 399))
        : serviceType === 'cat_grass' 
        ? (subscriptionLevel === 'premium' ? (currency === 'usd' ? 11.99 : 349) : (currency === 'usd' ? 7.99 : 249))
        : (subscriptionLevel === 'premium' ? (currency === 'usd' ? 42.99 : 1299) : (currency === 'usd' ? 32.99 : 999));
    }
  };
  
  // Helper function to get trial original price from config service or fallback to hardcoded
  const getTrialOriginalPriceFromConfig = (subscriptionLevel: string, currency: string) => {
    if (trialOriginalPricing) {
      const levelPricing = trialOriginalPricing[subscriptionLevel as 'standard' | 'premium'];
      return levelPricing?.[currency as 'thb' | 'usd'] || 
        (serviceType === 'flower'
          ? (subscriptionLevel === 'premium' 
            ? (currency === 'usd' ? 7.99 : 249)
            : (currency === 'usd' ? 5.99 : 149))
          : serviceType === 'cat_grass'
          ? (subscriptionLevel === 'premium'
            ? (currency === 'usd' ? 4.99 : 149)
            : (currency === 'usd' ? 2.99 : 99))
          : (subscriptionLevel === 'premium'
            ? (currency === 'usd' ? 15.99 : 549)
            : (currency === 'usd' ? 10.99 : 299)));
    } else {
      // Fallback to hardcoded values
      return serviceType === 'flower'
        ? (subscriptionLevel === 'premium' 
          ? (currency === 'usd' ? 7.99 : 249)
          : (currency === 'usd' ? 5.99 : 149))
        : serviceType === 'cat_grass'
        ? (subscriptionLevel === 'premium'
          ? (currency === 'usd' ? 4.99 : 149)
          : (currency === 'usd' ? 2.99 : 99))
        : (subscriptionLevel === 'premium'
          ? (currency === 'usd' ? 15.99 : 549)
          : (currency === 'usd' ? 10.99 : 299));
    }
  };
  
  // Original regular prices (not promotional)
  // Fallback to hardcoded values if not available in planDetails
  const originalStandardPrice_thb = planDetails?.standard?.original_price || 599;
  const originalPremiumPrice_thb = planDetails?.premium?.original_price || 999;
  
  // USD equivalents of original prices - now using configService
  const originalStandardPrice_usd = getOriginalPriceFromConfig('standard', 'usd'); 
  const originalPremiumPrice_usd = getOriginalPriceFromConfig('premium', 'usd');
  
  // Select the appropriate original price based on currency and subscription level
  const originalStandardPrice = useUSD ? originalStandardPrice_usd : originalStandardPrice_thb;
  const originalPremiumPrice = useUSD ? originalPremiumPrice_usd : originalPremiumPrice_thb;
  
  // Get plan prices from API data with currency consideration
  const standardPrice_thb = planDetails?.standard?.price || 599;
  const premiumPrice_thb = planDetails?.premium?.price || (599 * 1.5);
  
  const standardPrice_usd = planDetails?.standard?.price_usd || standardPrice_thb / 35; // Fallback conversion
  const premiumPrice_usd = planDetails?.premium?.price_usd || premiumPrice_thb / 35; // Fallback conversion
  
  // Get trial prices
  const standardTrialPrice_thb = planDetails?.standard?.trial_price || 199;
  const premiumTrialPrice_thb = planDetails?.premium?.trial_price || 299;
  
  const standardTrialPrice_usd = planDetails?.standard?.trial_price_usd || 5.99;
  const premiumTrialPrice_usd = planDetails?.premium?.trial_price_usd || 8.99;
  
  // Select the appropriate prices based on currency
  const standardPrice = useUSD ? standardPrice_usd : standardPrice_thb;
  const premiumPrice = useUSD ? premiumPrice_usd : premiumPrice_thb;
  
  const standardTrialPrice = useUSD ? standardTrialPrice_usd : standardTrialPrice_thb;
  const premiumTrialPrice = useUSD ? premiumTrialPrice_usd : premiumTrialPrice_thb;
  
  // Weekly price calculations (based on monthly price / 4 weeks)
  const standardWeeklyPrice = standardPrice / 4;
  const premiumWeeklyPrice = premiumPrice / 4;

  // Helper function to calculate discounted price
  const calculateDiscountedPrice = (originalPrice: number) => {
    // Skip promotion code discounts for trial subscriptions
    if (subscriptionType === 'trial') {
      return originalPrice;
    }
    
    // Check if promotion code is valid and applicable
    if (promotionDetails?.isValid && promotionDetails?.isLoaded && getEffectivePromotionCode) {
      const effectiveCode = getEffectivePromotionCode(originalPrice);
      if (effectiveCode && (!promotionDetails.currency || promotionDetails.currency === currency)) {
        if (promotionDetails.percentOff > 0) {
          return originalPrice * (1 - promotionDetails.percentOff / 100);
        }
        if (promotionDetails.amountOff > 0) {
          return Math.max(0, originalPrice - promotionDetails.amountOff);
        }
      }
    }
    
    return originalPrice;
  };

  // Helper function to check if promotion discount should be shown
  const shouldShowPromotionDiscount = (basePrice: number) => {
    // Skip for trial subscriptions
    if (subscriptionType === 'trial') {
      return false;
    }
    
    // Check if promotion is valid and loaded
    if (!promotionDetails?.isValid || !promotionDetails?.isLoaded || !getEffectivePromotionCode) {
      return false;
    }
    
    // Check if promotion code is effective for this price
    const effectiveCode = getEffectivePromotionCode(basePrice);
    if (!effectiveCode) {
      return false;
    }
    
    // Check currency compatibility
    if (promotionDetails.currency && promotionDetails.currency !== currency) {
      return false;
    }
    
    // Check if there's actually a discount
    return (promotionDetails.percentOff > 0 || promotionDetails.amountOff > 0);
  };

  return (
    <div className="bg-white">
      {loading ? (
        <div className="flex justify-center items-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F9D348]"></div>
        </div>
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          {/* Special launch offer tag - display for all currencies */}
          {displayMode === 'plan-level' && (
            <div className="flex items-center bg-blue-50 p-2 rounded-lg mb-4">
              <Tag size={16} className="text-blue-600 mr-2 flex-shrink-0" />
              <span className="text-blue-700 text-xs font-medium">
              {t('subscription.plansSection.launchSpecialOffer')}
              </span>
            </div>
          )}

          {/* Promotion Code Active Indicator */}
          {displayMode === 'plan-level' && promotionCode && promotionDetails?.isValid && promotionDetails?.isLoaded && (
            <div className="flex items-center bg-green-50 p-3 rounded-lg mb-4">
              <Gift size={16} className="text-green-600 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-green-700 text-sm font-medium">
                    {t('subscription.plansSection.promotionApplied', 'Promotion Applied')}
                  </span>
                  <span className="text-green-600 text-sm font-bold">
                    {promotionDetails?.percentOff > 0 
                      ? `${promotionDetails.percentOff}% ${t('subscription.plansSection.off', 'off')}`
                      : `${currencySymbol}${promotionDetails?.amountOff} ${t('subscription.plansSection.off', 'off')}`}
                  </span>
                </div>
                {promotionDetails.description && (
                  <div className="text-green-600 text-xs mt-1">
                    {promotionDetails.description}
                  </div>
                )}
                                 {/* Show estimated savings */}
                 {(() => {
                   const currentPrice = subscriptionLevel === 'premium' ? premiumPrice : standardPrice;
                   const savings = promotionDetails?.percentOff > 0 
                     ? currentPrice * (promotionDetails.percentOff / 100)
                     : promotionDetails?.amountOff || 0;
                   
                   if (savings > 0) {
                     return (
                       <div className="text-green-700 text-xs mt-1 font-medium">
                         {t('subscription.plansSection.youSave', 'You save')}: {currencySymbol}{formatPrice(savings, currency)}
                       </div>
                     );
                   }
                   return null;
                 })()}
              </div>
            </div>
          )}
        
          {/* Subscription Level */}
          {(displayMode === 'plan-level' || displayMode === 'both') && (
            <div className="mb-6">
              <div className="text-sm text-gray-600 mb-3">
                {serviceType === 'flower'
                  ? t('subscription.plansSection.bothPlansIncludeWeeklyFlowerDeliveriesOncePerWeek')
                  : serviceType === 'cat_grass'
                  ? t('subscription.plansSection.bothPlansIncludeWeeklyCatGrassDeliveriesOncePerWeek',
                      'Both plans include weekly cat grass deliveries, once per week.')
                  : t('subscription.plansSection.bothPlansIncludeWeeklyFruitDeliveriesOncePerWeek',
                      'Both plans include weekly fruit deliveries, once per week.')}
              </div>
              <div className="flex gap-4">
                <button
                  className={`flex-1 p-4 rounded-lg border-2 ${
                    subscriptionLevel === 'standard' 
                      ? 'border-[#F9D348] bg-yellow-50' 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  } transition-colors flex flex-col h-full`}
                  onClick={() => {
                    setSubscriptionLevel('standard');
                    analyticsService.trackSelectItem({
                      item_id: `${serviceType}_standard_plan`,
                      item_name: `${serviceType === 'flower' ? 'Flower' : serviceType === 'cat_grass' ? 'Cat Grass' : 'Fruit'} Standard Plan`,
                      item_category: 'Subscription Plan',
                      item_brand: 'Piika',
                      price: subscriptionType === 'trial' ? standardTrialPrice : standardPrice,
                      index: 0
                    }, 'subscription_plans', 'plan_selection');
                  }}
                >
                  {/* Image Container - Fixed height */}
                  <div className="flex justify-center mb-3 h-24 items-center w-full">
                    {serviceType === 'fruit' && (
                      <img src={standardFruitImage} alt="Standard Fruit" className="h-full object-contain mx-auto" />
                    )}
                    {serviceType === 'flower' && (
                      <img src={standardFlowerImage} alt="Standard Flower" className="h-12 object-contain mx-auto" />
                    )}
                    {serviceType === 'cat_grass' && (
                      <img src={catGrassSmallImage} alt="Standard Cat Grass" className="h-12 object-contain mx-auto" />
                    )}
                  </div>
                  
                  {/* Content Container - Flexible */}
                  <div className="flex flex-col flex-1">
                    <div className="text-lg font-semibold mb-2 text-gray-900">{t('subscription.plansSection.standardPlan')}</div>
                    <div className={`text-sm text-gray-600 mb-4 flex items-center ${
                      serviceType === 'flower' || serviceType === 'cat_grass' ? 'h-12' : 'h-20'
                    }`}>
                      {serviceType === 'flower'
                        ? t('subscription.plansSection.standardDescription')
                        : serviceType === 'cat_grass'
                        ? t('subscription.plansSection.standardCatGrassDescription',
                            'Perfect for single cat households.')
                        : t('subscription.plansSection.standardFruitDescription',
                            'Perfect for enjoying a variety of fresh fruits weekly.')}
                    </div>
                    
                    {/* Pricing Container - Consistent height */}
                    <div className="mt-auto">
                      {subscriptionType === 'trial' ? (
                        <>
                          <div className="text-sm text-gray-500 line-through mb-1">
                            {currencySymbol}{formatPrice(getTrialOriginalPriceFromConfig('standard', useUSD ? 'usd' : 'thb'), currency)}
                          </div>
                          <div className="text-xl font-bold text-green-600 mb-1">
                            {currencySymbol}{formatPrice(standardTrialPrice, currency)}
                          </div>
                          <div className="text-sm text-green-600 font-medium">
                            {t('subscription.plansSection.oneTime', 'one-time')}
                          </div>
                          <div className="text-xs text-blue-600 mt-1 font-medium">
                            {t('subscription.plansSection.oneDeliveryOnly', 'One delivery only')}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Show promotion discount if applicable */}
                          {shouldShowPromotionDiscount(standardPrice) ? (
                            <>
                              {/* Original price */}
                              <div className="text-sm text-gray-500 line-through mb-1">
                                {currencySymbol}{formatPrice(originalStandardPrice, currency)} {t('subscription.plansSection.perMonth')}
                              </div>
                              {/* Regular price (before promotion) */}
                              <div className="text-sm text-gray-400 line-through mb-1">
                                {currencySymbol}{formatPrice(standardPrice, currency)} {t('subscription.plansSection.perMonth')}
                              </div>
                              {/* Discounted price */}
                              <div className="text-xl font-bold text-green-600 mb-1">
                                {currencySymbol}{formatPrice(calculateDiscountedPrice(standardPrice), currency)}
                              </div>
                              <div className="text-sm text-green-600 font-medium mb-1">
                                {promotionDetails?.duration === 'once' 
                                  ? t('subscription.plansSection.firstMonth', 'First month')
                                  : t('subscription.plansSection.perMonth')}
                              </div>
                              {/* Promotion badge */}
                              <div className="text-xs text-orange-600 font-medium mb-1">
                                {promotionDetails?.percentOff > 0 
                                  ? `${promotionDetails.percentOff}% ${t('subscription.plansSection.off', 'off')}`
                                  : `${currencySymbol}${promotionDetails?.amountOff} ${t('subscription.plansSection.off', 'off')}`}
                              </div>
                              <div className="text-xs text-green-600 font-medium">
                                {currencySymbol}{formatPrice(calculateDiscountedPrice(standardPrice) / 4, currency)} {t('subscription.plansSection.perWeek')}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-sm text-gray-500 line-through mb-1">
                                {currencySymbol}{formatPrice(originalStandardPrice, currency)} {t('subscription.plansSection.perMonth')}
                              </div>
                              <div className="text-xl font-bold text-green-600 mb-1">
                                {currencySymbol}{formatPrice(standardPrice, currency)}
                              </div>
                              <div className="text-sm text-green-600 font-medium mb-1">
                                {t('subscription.plansSection.perMonth')}
                              </div>
                              <div className="text-xs text-green-600 font-medium">
                                {currencySymbol}{formatPrice(standardWeeklyPrice, currency)} {t('subscription.plansSection.perWeek')}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </button>
                <button
                  className={`flex-1 p-4 rounded-lg border-2 ${
                    subscriptionLevel === 'premium' 
                      ? 'border-[#F9D348] bg-yellow-50' 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  } transition-colors flex flex-col h-full`}
                  onClick={() => {
                    setSubscriptionLevel('premium');
                    analyticsService.trackSelectItem({
                      item_id: `${serviceType}_premium_plan`,
                      item_name: `${serviceType === 'flower' ? 'Flower' : serviceType === 'cat_grass' ? 'Cat Grass' : 'Fruit'} Premium Plan`,
                      item_category: 'Subscription Plan',
                      item_brand: 'Piika',
                      price: subscriptionType === 'trial' ? premiumTrialPrice : premiumPrice,
                      index: 1
                    }, 'subscription_plans', 'plan_selection');
                  }}
                >
                  {/* Image Container - Fixed height */}
                  <div className="flex justify-center mb-3 h-24 items-center w-full">
                    {serviceType === 'fruit' && (
                      <img src={premiumFruitImage} alt="Premium Fruit" className="h-full object-contain mx-auto" />
                    )}
                    {serviceType === 'flower' && (
                      <img src={premiumFlowerImage} alt="Premium Flower" className="h-16 object-contain mx-auto" />
                    )}
                    {serviceType === 'cat_grass' && (
                      <img src={catGrassSmallImage} alt="Premium Cat Grass" className="h-16 object-contain mx-auto" />
                    )}
                  </div>
                  
                  {/* Content Container - Flexible */}
                  <div className="flex flex-col flex-1">
                    <div className="text-lg font-semibold mb-2 text-gray-900">{t('subscription.plansSection.premiumPlan')}</div>
                    <div className={`text-sm text-gray-600 mb-4 flex items-center ${
                      serviceType === 'flower' || serviceType === 'cat_grass' ? 'h-12' : 'h-20'
                    }`}>
                      {serviceType === 'flower'
                        ? t('subscription.plansSection.premiumDescription')
                        : serviceType === 'cat_grass'
                        ? t('subscription.plansSection.premiumCatGrassDescription',
                            'Ideal for multiple cats or variety lovers.')
                        : t('subscription.plansSection.premiumFruitDescription',
                            'Luxurious selection of seasonal fruits.')}
                    </div>
                    
                    {/* Pricing Container - Consistent height */}
                    <div className="mt-auto">
                      {subscriptionType === 'trial' ? (
                        <>
                          <div className="text-sm text-gray-500 line-through mb-1">
                            {currencySymbol}{formatPrice(getTrialOriginalPriceFromConfig('premium', useUSD ? 'usd' : 'thb'), currency)}
                          </div>
                          <div className="text-xl font-bold text-green-600 mb-1">
                            {currencySymbol}{formatPrice(premiumTrialPrice, currency)}
                          </div>
                          <div className="text-sm text-green-600 font-medium">
                            {t('subscription.plansSection.oneTime', 'one-time')}
                          </div>
                          <div className="text-xs text-blue-600 mt-1 font-medium">
                            {t('subscription.plansSection.oneDeliveryOnly', 'One delivery only')}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Show promotion discount if applicable */}
                          {shouldShowPromotionDiscount(premiumPrice) ? (
                            <>
                              {/* Original price */}
                              <div className="text-sm text-gray-500 line-through mb-1">
                                {currencySymbol}{formatPrice(originalPremiumPrice, currency)} {t('subscription.plansSection.perMonth')}
                              </div>
                              {/* Regular price (before promotion) */}
                              <div className="text-sm text-gray-400 line-through mb-1">
                                {currencySymbol}{formatPrice(premiumPrice, currency)} {t('subscription.plansSection.perMonth')}
                              </div>
                              {/* Discounted price */}
                              <div className="text-xl font-bold text-green-600 mb-1">
                                {currencySymbol}{formatPrice(calculateDiscountedPrice(premiumPrice), currency)}
                              </div>
                              <div className="text-sm text-green-600 font-medium mb-1">
                                {promotionDetails?.duration === 'once' 
                                  ? t('subscription.plansSection.firstMonth', 'First month')
                                  : t('subscription.plansSection.perMonth')}
                              </div>
                              {/* Promotion badge */}
                              <div className="text-xs text-orange-600 font-medium mb-1">
                                {promotionDetails?.percentOff > 0 
                                  ? `${promotionDetails.percentOff}% ${t('subscription.plansSection.off', 'off')}`
                                  : `${currencySymbol}${promotionDetails?.amountOff} ${t('subscription.plansSection.off', 'off')}`}
                              </div>
                              <div className="text-xs text-green-600 font-medium">
                                {currencySymbol}{formatPrice(calculateDiscountedPrice(premiumPrice) / 4, currency)} {t('subscription.plansSection.perWeek')}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-sm text-gray-500 line-through mb-1">
                                {currencySymbol}{formatPrice(originalPremiumPrice, currency)} {t('subscription.plansSection.perMonth')}
                              </div>
                              <div className="text-xl font-bold text-green-600 mb-1">
                                {currencySymbol}{formatPrice(premiumPrice, currency)}
                              </div>
                              <div className="text-sm text-green-600 font-medium mb-1">
                                {t('subscription.plansSection.perMonth')}
                              </div>
                              <div className="text-xs text-green-600 font-medium">
                                {currencySymbol}{formatPrice(premiumWeeklyPrice, currency)} {t('subscription.plansSection.perWeek')}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Subscription Type Selection */}
          {(displayMode === 'billing-period' || displayMode === 'both') && (
            <div className="space-y-3">
              <div className="text-sm text-gray-500 mb-3">{t('subscription.plansSection.billingPeriod')}</div>
              
              {/* Trial Option Card */}
              <div 
                className={`border-2 rounded-lg transition-colors relative ${
                  subscriptionType === 'trial' 
                    ? 'border-[#F9D348] bg-[#FFF9E6]' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {subscriptionType !== 'trial' && (
                  <div className="absolute -top-2 left-2 bg-blue-100 text-blue-800 text-xs font-medium px-3 py-0.5 rounded-full z-10 whitespace-nowrap transform -translate-y-1">
                    {t('subscription.plansSection.tryFirst', 'Try First')}
                  </div>
                )}
                
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => {
                    setSubscriptionType('trial');
                    analyticsService.trackSelectItem({
                      item_id: `${serviceType}_trial_option`,
                      item_name: `${serviceType === 'flower' ? 'Flower' : serviceType === 'cat_grass' ? 'Cat Grass' : 'Fruit'} Trial Option`,
                      item_category: 'Billing Type',
                      item_brand: 'Piika',
                      price: subscriptionLevel === 'premium' ? premiumTrialPrice : standardTrialPrice,
                      index: 0
                    }, 'billing_options', 'billing_selection');
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        subscriptionType === 'trial' ? 'border-[#F9D348]' : 'border-gray-300'
                      }`}>
                        {subscriptionType === 'trial' && (
                          <div className="w-3 h-3 rounded-full bg-[#F9D348]"></div>
                        )}
                      </div>
                      <div className="ml-3">
                        <h3 className="text-base font-medium">{t('subscription.plansSection.trialOption', 'One-Time Trial')}</h3>
                        <p className="text-sm text-gray-500 mt-1">{t('subscription.plansSection.trialDescription', 'Try our service with one delivery')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500 line-through block">
                        {currencySymbol}{formatPrice(getTrialOriginalPriceFromConfig(subscriptionLevel, useUSD ? 'usd' : 'thb'), currency)}
                      </span>
                      <span className="text-base font-medium text-green-600">
                        {currencySymbol}{formatPrice(subscriptionLevel === 'premium' ? premiumTrialPrice : standardTrialPrice, currency)}
                      </span>
                      <p className="text-xs text-blue-600 mt-1">{t('subscription.plansSection.oneDeliveryOnly', 'One delivery only')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Monthly Billing Card */}
              <div 
                className={`border-2 rounded-lg transition-colors ${
                  subscriptionType === 'monthly' 
                    ? 'border-[#F9D348] bg-[#FFF9E6]' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => {
                    setSubscriptionType('monthly');
                    analyticsService.trackSelectItem({
                      item_id: `${serviceType}_monthly_billing`,
                      item_name: `${serviceType === 'flower' ? 'Flower' : serviceType === 'cat_grass' ? 'Cat Grass' : 'Fruit'} Monthly Billing`,
                      item_category: 'Billing Type',
                      item_brand: 'Piika',
                      price: subscriptionLevel === 'premium' ? premiumPrice : standardPrice,
                      index: 1
                    }, 'billing_options', 'billing_selection');
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        subscriptionType === 'monthly' ? 'border-[#F9D348]' : 'border-gray-300'
                      }`}>
                        {subscriptionType === 'monthly' && (
                          <div className="w-3 h-3 rounded-full bg-[#F9D348]"></div>
                        )}
                      </div>
                      <div className="ml-3">
                        <h3 className="text-base font-medium">{t('subscription.plansSection.monthlyBilling')}</h3>
                        <p className="text-sm text-gray-500 mt-1">{t('subscription.plansSection.billingPeriodMonthly')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {/* Show promotion discount if applicable */}
                      {shouldShowPromotionDiscount(subscriptionLevel === 'premium' ? premiumPrice : standardPrice) ? (
                        <>
                          {/* Original price */}
                          <span className="text-xs text-gray-500 line-through block">
                            {currencySymbol}{formatPrice(subscriptionLevel === 'premium' ? originalPremiumPrice : originalStandardPrice, currency)}
                          </span>
                          {/* Regular price (before promotion) */}
                          <span className="text-xs text-gray-400 line-through block">
                            {currencySymbol}{formatPrice(subscriptionLevel === 'premium' ? premiumPrice : standardPrice, currency)}
                          </span>
                          {/* Discounted price */}
                          <span className="text-base font-medium text-green-600">
                            {currencySymbol}{formatPrice(calculateDiscountedPrice(subscriptionLevel === 'premium' ? premiumPrice : standardPrice), currency)}
                          </span>
                          {/* Promotion badge */}
                          <p className="text-xs text-orange-600 font-medium mt-1">
                            {promotionDetails?.percentOff > 0 
                              ? `${promotionDetails.percentOff}% ${t('subscription.plansSection.off', 'off')}`
                              : `${currencySymbol}${promotionDetails?.amountOff} ${t('subscription.plansSection.off', 'off')}`}
                          </p>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-gray-500 line-through block">
                            {currencySymbol}{formatPrice(subscriptionLevel === 'premium' ? originalPremiumPrice : originalStandardPrice, currency)}
                          </span>
                          <span className="text-base font-medium text-green-600">{currencySymbol}{formatPrice(subscriptionLevel === 'premium' ? premiumPrice : standardPrice, currency)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Nested Payment Type Selection for Monthly */}
                {subscriptionType === 'monthly' && (
                  <div className="border-t border-gray-100 bg-white">
                    <PaymentTypeSection
                      paymentType={paymentType}
                      setPaymentType={setPaymentType}
                      subscriptionType="monthly"
                      subscriptionLevel={subscriptionLevel}
                      premiumPrice={premiumPrice}
                      standardPrice={standardPrice}
                      disableAutoRenewal={selectedPaymentMethod === 'promptpay'}
                      currencySymbol={currencySymbol}
                      currency={currency}
                    />
                  </div>
                )}
              </div>
              
              {/* Annual Billing Card */}
              <div 
                className={`border-2 rounded-lg transition-colors relative ${
                  subscriptionType === 'yearly' 
                    ? 'border-[#F9D348] bg-[#FFF9E6]' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {subscriptionType !== 'yearly' && (
                  <div className="absolute -top-2 right-2 bg-green-100 text-green-800 text-xs font-medium px-3 py-0.5 rounded-full z-10 whitespace-nowrap transform -translate-y-1">
                    {t('subscription.plansSection.bestValue')}
                  </div>
                )}
                
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => {
                    setSubscriptionType('yearly');
                    analyticsService.trackSelectItem({
                      item_id: `${serviceType}_yearly_billing`,
                      item_name: `${serviceType === 'flower' ? 'Flower' : serviceType === 'cat_grass' ? 'Cat Grass' : 'Fruit'} Yearly Billing`,
                      item_category: 'Billing Type',
                      item_brand: 'Piika',
                      price: subscriptionLevel === 'premium' ? premiumPrice : standardPrice,
                      index: 2
                    }, 'billing_options', 'billing_selection');
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        subscriptionType === 'yearly' ? 'border-[#F9D348]' : 'border-gray-300'
                      }`}>
                        {subscriptionType === 'yearly' && (
                          <div className="w-3 h-3 rounded-full bg-[#F9D348]"></div>
                        )}
                      </div>
                      <div className="ml-3">
                        <h3 className="text-base font-medium">{t('subscription.plansSection.annualBilling')}</h3>
                        <p className="text-sm text-gray-500 mt-1">{t('subscription.plansSection.billingPeriodYearly')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {/* Calculate yearly base price */}
                      {(() => {
                        const yearlyBasePrice = subscriptionLevel === 'premium' ? (premiumPrice * 12 * 0.9) : (standardPrice * 12 * 0.9);
                        return shouldShowPromotionDiscount(yearlyBasePrice) ? (
                          <>
                            {/* Original yearly price */}
                            <span className="text-xs text-gray-500 line-through block">
                              {currencySymbol}{formatPrice(subscriptionLevel === 'premium' ? (originalPremiumPrice * 12) : (originalStandardPrice * 12), currency)}
                            </span>
                            {/* Regular yearly price (before promotion) */}
                            <span className="text-xs text-gray-400 line-through block">
                              {currencySymbol}{formatPrice(yearlyBasePrice, currency)}
                            </span>
                            {/* Discounted yearly price */}
                            <span className="text-base font-medium text-green-600">
                              {currencySymbol}{formatPrice(calculateDiscountedPrice(yearlyBasePrice), currency)}
                            </span>
                            {/* Promotion badge */}
                            <p className="text-xs text-orange-600 font-medium mt-1">
                              {promotionDetails?.percentOff > 0 
                                ? `${promotionDetails.percentOff}% ${t('subscription.plansSection.off', 'off')}`
                                : `${currencySymbol}${promotionDetails?.amountOff} ${t('subscription.plansSection.off', 'off')}`}
                            </p>
                            <p className="text-xs text-green-600 mt-1">{t('subscription.plansSection.yearlyDiscount')}</p>
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-gray-500 line-through block">
                              {currencySymbol}{formatPrice(subscriptionLevel === 'premium' ? (originalPremiumPrice * 12) : (originalStandardPrice * 12), currency)}
                            </span>
                            <span className="text-base font-medium text-green-600">{currencySymbol}{formatPrice(yearlyBasePrice, currency)}</span>
                            <p className="text-xs text-green-600 mt-1">{t('subscription.plansSection.yearlyDiscount')}</p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* Nested Payment Type Selection for Annual */}
                {subscriptionType === 'yearly' && (
                  <div className="border-t border-gray-100 bg-white">
                    <PaymentTypeSection
                      paymentType={paymentType}
                      setPaymentType={setPaymentType}
                      subscriptionType="yearly"
                      subscriptionLevel={subscriptionLevel}
                      premiumPrice={premiumPrice}
                      standardPrice={standardPrice}
                      disableAutoRenewal={selectedPaymentMethod === 'promptpay'}
                      currencySymbol={currencySymbol}
                      currency={currency}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Yearly Benefits (Collapsible) */}
          {subscriptionType === 'yearly' && serviceType === 'flower' && (displayMode === 'billing-period' || displayMode === 'both') && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-green-600 mb-2">
                <Gift size={16} />
                <span>{t('subscription.paymentMethods.yearlySubscriptionBenefits')}</span>
              </div>
                <div className="flex items-center gap-2">
                  <img src={vaseImage} alt="Premium Vase" className="w-8 h-8 object-contain" />
                  <div>
                    <div className="text-sm font-medium">{t('subscription.paymentMethods.freePremiumVase')}</div>
                    <div className="text-xs text-gray-500">{t('subscription.paymentMethods.premiumVaseInfo')}</div>
                  </div>
                </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubscriptionPlanSection; 